import { useEffect, useMemo, useRef, useState } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Header from "@/components/Header";
import MainWorkspaceLayout from "@/components/workspace/MainWorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { EntityListPanel } from "@/components/shared/EntityListPanel";
import { EntityListPaneHeader } from "@/components/shared/EntityListPaneHeader";
import { EntitySearchInput } from "@/components/shared/EntitySearchInput";
import { EntityDetailsShell } from "@/components/shared/EntityDetailsShell";
import { EntityGeometrySection } from "@/components/shared/EntityGeometrySection";
import { ModuleWorkspaceMapPanel } from "@/components/module-map/ModuleWorkspaceMapPanel";
import { GoEntityComparePanel } from "@/components/go/GoEntityComparePanel";
import { CountryPassportPanel } from "@/components/go/CountryPassportPanel";
import { CountryFieldsBrowser } from "@/components/go/CountryFieldsBrowser";
import { CountryClassifierPanel } from "@/components/go/CountryClassifierPanel";
import { CountryMaterialsGallery } from "@/components/go/CountryMaterialsGallery";
import { PrepareClassifierDialog } from "@/components/go/PrepareClassifierDialog";
import { GoCardFieldControl } from "@/components/go/GoCardFieldControl";
import { GoGeneralInfoTree } from "@/components/go/GoGeneralInfoTree";
import {
  COUNTRY_PASSPORT_FIELDS,
  formatPassportValue,
} from "@/components/go/countryPassportFields";
import { RegionHierarchyHint } from "@/components/go/RegionHierarchyHint";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronDown, ListFilter, Plus, Printer, Trash2 } from "lucide-react";
import { goEntitiesApi } from "@/services/go-entities.service";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { notifyModuleError, notifyModuleSuccess } from "@/lib/module-toast";
import {
  blobToDataUrl,
  printGoCountryPassportReport,
} from "@/lib/go-country-passport-print";
import { chromeSelectClass } from "@/lib/map-chrome";
import { cn } from "@/lib/utils";
import type { Geometry } from "@/types/api-v2";
import type {
  GoEntityMapGeometryItem,
  GoEntityPatchPayload,
  GoGeneralInfoTreeNode,
  GoMaterialItem,
} from "@/types/go-entity";
import type { GoEntityCardFieldValue, GoFieldProvenancePatch } from "@/types/go-entity-card";

type GoSidebarTab = "countries" | "general-info";

const PASSPORT_FIELD_KEYS = new Set(
  COUNTRY_PASSPORT_FIELDS.flatMap((d) => [d.fieldKey, ...(d.altKeys ?? [])]),
);

export default function GoWorkspacePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => {
    const raw = searchParams.get("q");
    // защита от мусора в URL (?q=null) — иначе в поле поиска видно «null»
    if (!raw || raw === "null" || raw === "undefined") return "";
    return raw;
  });
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const raw = searchParams.get("entityId");
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  const [compareRightId, setCompareRightId] = useState<number | null>(() => {
    const raw = searchParams.get("compareRight");
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  const [nameInput, setNameInput] = useState("");
  const [sectionIdInput, setSectionIdInput] = useState<string>("none");
  const [level1IdInput, setLevel1IdInput] = useState<string>("none");
  const [level2IdInput, setLevel2IdInput] = useState<string>("none");
  const [entityTypeIdInput, setEntityTypeIdInput] = useState<string>("none");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [createNameInput, setCreateNameInput] = useState("");
  const [createEntityTypeIdInput, setCreateEntityTypeIdInput] = useState<string>("none");
  const [createSectionIdInput, setCreateSectionIdInput] = useState<string>("none");
  const [createLevel1IdInput, setCreateLevel1IdInput] = useState<string>("none");
  const [createLevel2IdInput, setCreateLevel2IdInput] = useState<string>("none");
  const [bindGeometryName, setBindGeometryName] = useState("");
  const [bindGeometryId, setBindGeometryId] = useState("");
  const [reverseLon, setReverseLon] = useState("37.658583");
  const [reverseLat, setReverseLat] = useState("55.740095");
  const [linkEudgId, setLinkEudgId] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const [cardEdit, setCardEdit] = useState(false);
  const [passportPrintPending, setPassportPrintPending] = useState(false);
  const [prepareOpen, setPrepareOpen] = useState(false);
  const [continueNonce, setContinueNonce] = useState(0);
  const [shortcutsHelp, setShortcutsHelp] = useState(false);
  const [savingFieldKeys, setSavingFieldKeys] = useState<Set<string>>(() => new Set());
  const [highlightFieldKey, setHighlightFieldKey] = useState<string | null>(() => {
    const raw = searchParams.get("field");
    return raw && raw.trim() ? raw.trim() : null;
  });
  const fieldDeepLinkApplied = useRef(false);
  const [mapPreviewGeometry, setMapPreviewGeometry] = useState<Geometry | null>(null);
  const [mapPreviewNonce, setMapPreviewNonce] = useState(0);
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [deletedFilter, setDeletedFilter] = useState<"active" | "include" | "deleted_only">("active");
  const [hasUdgFilter, setHasUdgFilter] = useState<"all" | "yes" | "no">("all");
  const [sidebarTab, setSidebarTab] = useState<GoSidebarTab>("countries");
  const [generalInfoSearch, setGeneralInfoSearch] = useState("");
  const [generalInfoSelection, setGeneralInfoSelection] = useState<{
    klsId: number;
    code: string;
    label: string;
  } | null>(null);
  const mapPanelRef = useRef<ImperativePanelHandle>(null);
  const isMobile = useIsMobile();
  const hubTabForCard =
    activeTab === "geography" || activeTab === "society" || activeTab === "economy"
      ? activeTab
      : activeTab === "general"
        ? "passport"
        : undefined;

  useEffect(() => {
    if (!isMobile) return;
    setMapCollapsed(true);
    mapPanelRef.current?.collapse();
  }, [isMobile]);

  const metaQuery = useQuery({
    queryKey: ["go-entities", "meta"],
    queryFn: () => goEntitiesApi.getMeta(),
  });
  const countryTypeKlsId = metaQuery.data?.country_entity_type_kls_id ?? null;

  const listQuery = useQuery({
    queryKey: ["go-entities", "list", searchTerm, deletedFilter, hasUdgFilter, countryTypeKlsId],
    queryFn: () =>
      goEntitiesApi.list({
        limit: 100,
        q: searchTerm || undefined,
        include_deleted: deletedFilter === "include",
        deleted_only: deletedFilter === "deleted_only",
        ...(hasUdgFilter === "yes" ? { has_udgspz: true } : {}),
        ...(hasUdgFilter === "no" ? { has_udgspz: false } : {}),
        ...(countryTypeKlsId != null ? { entity_type_kls_id: countryTypeKlsId } : {}),
      }),
    enabled: metaQuery.isFetched,
  });
  const entities = listQuery.data?.items ?? [];
  const countriesTotal = listQuery.data?.total ?? entities.length;

  const generalInfoTreeQuery = useQuery({
    queryKey: ["go-entities", "general-info-tree"],
    queryFn: () => goEntitiesApi.getGeneralInfoTree(),
    enabled: sidebarTab === "general-info",
  });
  const generalInfoResolveQuery = useQuery({
    queryKey: ["go-entities", "general-info-resolve", generalInfoSelection?.klsId],
    queryFn: () => goEntitiesApi.resolveGeneralInfoSection(generalInfoSelection!.klsId),
    enabled: generalInfoSelection != null && selectedId == null,
  });
  const generalInfoEntitiesQuery = useQuery({
    queryKey: [
      "go-entities",
      "general-info-entities",
      generalInfoSelection?.klsId,
      generalInfoResolveQuery.data?.section_kls_id,
    ],
    queryFn: () =>
      goEntitiesApi.list({
        limit: 200,
        general_info_tree_kls_id: generalInfoSelection!.klsId,
        roots_only: true,
      }),
    enabled:
      generalInfoSelection != null &&
      selectedId == null &&
      generalInfoResolveQuery.data?.resolved === true,
  });

  const selectedEntity = useMemo(
    () =>
      entities.find((item) => item.ginf_obj_id === selectedId) ??
      generalInfoEntitiesQuery.data?.items.find((item) => item.ginf_obj_id === selectedId) ??
      null,
    [entities, generalInfoEntitiesQuery.data?.items, selectedId],
  );
  const isSelectedDeleted = Boolean(selectedEntity?.is_del);
  const detailQuery = useQuery({
    queryKey: ["go-entities", "detail", selectedId],
    queryFn: () => goEntitiesApi.getById(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted,
    placeholderData: (previousData) => previousData,
  });
  const materialsQuery = useQuery({
    queryKey: ["go-entities", "materials", selectedId],
    queryFn: () => goEntitiesApi.listMaterials(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted,
    placeholderData: (previousData) => previousData,
  });
  const regionLinksQuery = useQuery({
    queryKey: ["go-entities", "region-links", selectedId],
    queryFn: () => goEntitiesApi.listRegionLinks(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted,
    placeholderData: (previousData) => previousData,
  });
  const linkedCountriesQuery = useQuery({
    queryKey: ["go-entities", "linked-countries", selectedId],
    queryFn: () => goEntitiesApi.listLinkedCountries(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted,
    placeholderData: (previousData) => previousData,
  });
  // Не берём map_geometries из placeholderData предыдущей страны — иначе fit/оверлей «на чужой» контур.
  const mapGeometries =
    selectedId != null && detailQuery.data?.ginf_obj_id === selectedId
      ? (detailQuery.data.map_geometries ?? [])
      : [];
  const overlayQuery = useQuery({
    queryKey: [
      "go-entities",
      "overlay",
      selectedId,
      mapGeometries.map((g) => g.ginf_obj_map_geom_id),
    ],
    queryFn: async () => {
      if (selectedId == null) return [];
      try {
        return await goEntitiesApi.listMapGeometryContours(selectedId);
      } catch {
        return [];
      }
    },
    enabled: selectedId != null && !isSelectedDeleted && mapGeometries.length > 0,
  });
  const compareQuery = useQuery({
    queryKey: ["go-entities", "compare", selectedId, compareRightId],
    queryFn: () => goEntitiesApi.compare(selectedId!, compareRightId!),
    enabled:
      activeTab === "compare" &&
      selectedId != null &&
      !isSelectedDeleted &&
      compareRightId != null &&
      selectedId !== compareRightId,
  });
  const compareOverlayQuery = useQuery({
    queryKey: [
      "go-entities",
      "compare-overlay",
      selectedId,
      compareRightId,
      compareQuery.data?.left.map_geometry_ids,
      compareQuery.data?.right.map_geometry_ids,
    ],
    queryFn: async () => {
      const entityIds = [selectedId, compareRightId].filter(
        (id): id is number => id != null,
      );
      const batches = await Promise.all(
        entityIds.map(async (id) => {
          try {
            return await goEntitiesApi.listMapGeometryContours(id);
          } catch {
            return [] as Geometry[];
          }
        }),
      );
      const byId = new Map<number, Geometry>();
      for (const batch of batches) {
        for (const g of batch) byId.set(g.id, g);
      }
      return [...byId.values()];
    },
    enabled:
      activeTab === "compare" &&
      Boolean(compareQuery.data) &&
      ((compareQuery.data?.left.map_geometry_ids.length ?? 0) > 0 ||
        (compareQuery.data?.right.map_geometry_ids.length ?? 0) > 0),
  });

  const overlayGeometriesRaw =
    activeTab === "compare" ? (compareOverlayQuery.data ?? []) : (overlayQuery.data ?? []);
  /**
   * MapLibre `buildObjectGeometryFeatures` отбрасывает фичи, если `geometry.object_id !== selectedObject.id`.
   * У ГО selectedObject.id = ginf_obj_id, а у core.geometries.object_id — id объекта-носителя.
   * Ремапим object_id, иначе оверлей пустой и fit не срабатывает.
   */
  const overlayGeometries = useMemo(() => {
    if (selectedId == null) return [];
    return overlayGeometriesRaw.map((g) =>
      g.object_id === selectedId ? g : { ...g, object_id: selectedId },
    );
  }, [overlayGeometriesRaw, selectedId]);
  /**
   * Держим selectedObject на время загрузки overlay: иначе panel-эффект clear срывает
   * только что запрошенный focusGeometryOnMap при смене/выборе страны.
   */
  const overlaySelectedObject = useMemo(() => {
    if (selectedId == null) return null;
    return {
      id: selectedId,
      name:
        selectedEntity?.obj_name ||
        detailQuery.data?.obj_name ||
        `Сущность #${selectedId}`,
      kls_id: null,
      organization_id: null,
      status: "active",
    };
  }, [selectedId, selectedEntity?.obj_name, detailQuery.data?.obj_name]);
  const overlayFocusKey = useMemo(
    () =>
      selectedId == null || overlayGeometries.length === 0
        ? ""
        : `${selectedId}:${overlayGeometries
            .map((g) => g.id)
            .slice()
            .sort((a, b) => a - b)
            .join(",")}`,
    [selectedId, overlayGeometries],
  );
  const lastMapFocusKeyRef = useRef("");

  useEffect(() => {
    setCardEdit(false);
    fieldDeepLinkApplied.current = false;
    // Смена сущности: сбросить ключ фокуса, чтобы новый overlay гарантированно вызвал fitBounds.
    lastMapFocusKeyRef.current = "";
  }, [selectedId]);

  useEffect(() => {
    if (selectedId != null) return;
    lastMapFocusKeyRef.current = "";
    setMapPreviewGeometry(null);
    setMapPreviewNonce(0);
    // Явный deselect: снять sticky highlight/selected (событие + повторы внутри MapLibreMap).
    window.dispatchEvent(new CustomEvent("clearMapGeometryHighlight"));
    const t = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("clearMapGeometryHighlight"));
    }, 100);
    return () => window.clearTimeout(t);
  }, [selectedId]);

  /** Показать контуры и fitBounds при выборе/смене страны (явный preview + autofit selected-geometries). */
  useEffect(() => {
    if (!overlayFocusKey || selectedId == null || isSelectedDeleted) {
      lastMapFocusKeyRef.current = "";
      return;
    }
    if (lastMapFocusKeyRef.current === overlayFocusKey) return;
    lastMapFocusKeyRef.current = overlayFocusKey;

    const primary = overlayGeometries.find((g) => Boolean(g.geojson)) ?? null;
    if (!primary) return;

    if (mapCollapsed) {
      mapPanelRef.current?.expand();
      setMapCollapsed(false);
    }
    setMapPreviewGeometry(primary);
    setMapPreviewNonce((n) => n + 1);
  }, [
    overlayFocusKey,
    selectedId,
    isSelectedDeleted,
    overlayGeometries,
    mapCollapsed,
  ]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (compareRightId != null) next.set("compareRight", String(compareRightId));
    else next.delete("compareRight");
    if (selectedId != null) next.set("entityId", String(selectedId));
    else next.delete("entityId");
    if (highlightFieldKey) next.set("field", highlightFieldKey);
    else next.delete("field");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [compareRightId, selectedId, highlightFieldKey, searchParams, setSearchParams]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (e.key === "Escape") {
        if (cardEdit) {
          setCardEdit(false);
          return;
        }
        setSelectedId(null);
        setCompareRightId(null);
        setHighlightFieldKey(null);
        return;
      }
      if (!selectedId || isSelectedDeleted) return;
      if (mod && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setActiveTab("classifier");
        setContinueNonce((n) => n + 1);
        return;
      }
      if (mod && e.key === "/") {
        e.preventDefault();
        setShortcutsHelp(true);
        return;
      }
      if (mod && e.shiftKey && e.key.toLowerCase() === "n") {
        if (!cardEdit || !highlightFieldKey) return;
        e.preventDefault();
        void markNaField(highlightFieldKey);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // markNaField defined below — intentional stable invalidate via selectedId/cardEdit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardEdit, selectedId, isSelectedDeleted, highlightFieldKey]);

  useEffect(() => {
    if (!highlightFieldKey || !selectedId || fieldDeepLinkApplied.current) return;
    if (PASSPORT_FIELD_KEYS.has(highlightFieldKey)) {
      setActiveTab("general");
    } else {
      setActiveTab("classifier");
    }
    fieldDeepLinkApplied.current = true;
  }, [highlightFieldKey, selectedId]);

  const cardQuery = useQuery({
    queryKey: ["go-entities", "card", selectedId, hubTabForCard ?? "all"],
    queryFn: () => goEntitiesApi.getCard(selectedId!, hubTabForCard),
    enabled:
      selectedId != null &&
      !isSelectedDeleted &&
      (activeTab === "general" ||
        activeTab === "geography" ||
        activeTab === "society" ||
        activeTab === "economy" ||
        activeTab === "all-fields" ||
        activeTab === "classifier"),
    placeholderData: (previousData) => previousData,
  });
  const cardAuditQuery = useQuery({
    queryKey: ["go-entities", "card-audit", selectedId],
    queryFn: () => goEntitiesApi.listCardAudit(selectedId!, { limit: 50 }),
    enabled: selectedId != null && !isSelectedDeleted && activeTab === "general",
  });
  const summaryQuery = useQuery({
    queryKey: ["go-entities", "summary", selectedId],
    queryFn: () => goEntitiesApi.getSummary(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted && activeTab === "general",
  });
  const withFieldSaving = async (
    fieldKey: string,
    run: () => Promise<void>,
    okTitle: string,
    errLabel: string,
  ) => {
    if (selectedId == null) return { ok: false as const };
    setSavingFieldKeys((prev) => new Set(prev).add(fieldKey));
    try {
      await run();
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "card", selectedId] });
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "card-audit", selectedId] });
      notifyModuleSuccess(toast, okTitle);
      return { ok: true as const };
    } catch (e) {
      notifyModuleError(toast, errLabel, e);
      return { ok: false as const };
    } finally {
      setSavingFieldKeys((prev) => {
        const next = new Set(prev);
        next.delete(fieldKey);
        return next;
      });
    }
  };

  const commitCardField = async (fieldKey: string, value: unknown) =>
    withFieldSaving(
      fieldKey,
      async () => {
        await goEntitiesApi.putCard(selectedId!, { values: { [fieldKey]: value } });
      },
      "Поле карточки сохранено",
      "сохранение поля карточки ГО",
    );

  const saveFieldProvenance = async (fieldKey: string, patch: GoFieldProvenancePatch) =>
    withFieldSaving(
      fieldKey,
      async () => {
        await goEntitiesApi.putCard(selectedId!, { provenance: { [fieldKey]: patch } });
      },
      "Сведения об источнике сохранены",
      "сохранение provenance поля ГО",
    );

  const markNaField = async (fieldKey: string) =>
    withFieldSaving(
      fieldKey,
      async () => {
        await goEntitiesApi.putCard(selectedId!, { mark_na: [fieldKey] });
      },
      "Поле помечено N/A",
      "пометку N/A",
    );

  const clearNaField = async (fieldKey: string) =>
    withFieldSaving(
      fieldKey,
      async () => {
        await goEntitiesApi.putCard(selectedId!, { clear_na: [fieldKey] });
      },
      "N/A снято",
      "снятие N/A",
    );

  const patchMutation = useMutation({
    mutationFn: async (payload: GoEntityPatchPayload) => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      return goEntitiesApi.patch(selectedId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "detail", selectedId] });
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "list"] });
      notifyModuleSuccess(toast, "Карточка ГО обновлена");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "обновление карточки ГО", e);
    },
  });
  const createMapGeometryMutation = useMutation({
    mutationFn: async (mapGeometryId: number) => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      return goEntitiesApi.createMapGeometry(selectedId, { map_geometry_id: mapGeometryId });
    },
    onSuccess: async () => {
      setBindGeometryId("");
      setBindGeometryName("");
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "detail", selectedId] });
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "list"] });
      notifyModuleSuccess(toast, "Геометрия привязана");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "привязку геометрии ГО", e);
    },
  });
  const deleteMapGeometryMutation = useMutation({
    mutationFn: async (ginfObjMapGeomId: number) => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      return goEntitiesApi.deleteMapGeometry(selectedId, ginfObjMapGeomId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "detail", selectedId] });
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "list"] });
      notifyModuleSuccess(toast, "Привязка геометрии удалена");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "удаление привязки геометрии ГО", e);
    },
  });
  const reverseGeocodeMutation = useMutation({
    mutationFn: async () => {
      const longitude = Number(reverseLon);
      const latitude = Number(reverseLat);
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        throw new Error("Укажите корректные longitude/latitude");
      }
      return goEntitiesApi.reverseGeocode({ longitude, latitude, include_geography: true });
    },
    onSuccess: () => {
      notifyModuleSuccess(toast, "Reverse-geocode выполнен");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "reverse-geocode ГО", e);
    },
  });
  const createRegionLinkMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      const eudgId = Number(linkEudgId);
      if (!Number.isFinite(eudgId) || eudgId < 1) throw new Error("Укажите eudg_id");
      return goEntitiesApi.createRegionLink(selectedId, { eudg_id: eudgId, coverage: "PARTIAL" });
    },
    onSuccess: async () => {
      setLinkEudgId("");
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "region-links", selectedId] });
      notifyModuleSuccess(toast, "Связь с регионом создана");
    },
    onError: (e: Error) => notifyModuleError(toast, "создание связи ГО↔регион", e),
  });
  const refreshRegionLinksMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      return goEntitiesApi.refreshRegionLinks(selectedId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "region-links", selectedId] });
      notifyModuleSuccess(toast, "Связи ГО↔регион обновлены");
    },
    onError: (e: Error) => notifyModuleError(toast, "refresh связей ГО↔регион", e),
  });
  const deleteRegionLinkMutation = useMutation({
    mutationFn: async (linkId: number) => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      return goEntitiesApi.deleteRegionLink(selectedId, linkId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "region-links", selectedId] });
      notifyModuleSuccess(toast, "Связь удалена");
    },
    onError: (e: Error) => notifyModuleError(toast, "удаление связи ГО↔регион", e),
  });
  const softDeleteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      return goEntitiesApi.softDelete(selectedId);
    },
    onSuccess: async () => {
      setDeleteConfirmOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "list"] });
      setSelectedId(null);
      notifyModuleSuccess(toast, "Сущность ГО удалена");
    },
    onError: (e: Error) => notifyModuleError(toast, "удаление сущности ГО", e),
  });
  const restoreMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      return goEntitiesApi.restore(selectedId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["go-entities"] });
      notifyModuleSuccess(toast, "Сущность ГО восстановлена");
    },
    onError: (e: Error) => notifyModuleError(toast, "восстановление сущности ГО", e),
  });
  const createEntityMutation = useMutation({
    mutationFn: async (payload: {
      obj_name: string;
      kls_id_section: number;
      entity_type_kls_id: number;
      kls_id_1?: number | null;
      kls_id_2?: number | null;
    }) => goEntitiesApi.create(payload),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "list"] });
      setSelectedId(created.ginf_obj_id);
      setGeneralInfoSelection(null);
      setSidebarTab("countries");
      setActiveTab("general");
      setCreateDialogOpen(false);
      setCreateNameInput("");
      setCreateEntityTypeIdInput("none");
      setCreateLevel1IdInput("none");
      setCreateLevel2IdInput("none");
      notifyModuleSuccess(toast, "Сущность ГО создана");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "создание сущности ГО", e);
    },
  });
  const uploadMaterialMutation = useMutation({
    mutationFn: async (payload: { file: File; materialKind: string | null }) => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      return goEntitiesApi.uploadMaterial(selectedId, payload.file, {
        materialKind: payload.materialKind,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "materials", selectedId] });
      notifyModuleSuccess(toast, "Файл загружен");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "загрузку материала ГО", e);
    },
  });
  const deleteMaterialMutation = useMutation({
    mutationFn: async (ginfFileId: number) => {
      if (!selectedId) throw new Error("ginf_obj_id is required");
      return goEntitiesApi.deleteMaterial(selectedId, ginfFileId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["go-entities", "materials", selectedId] });
      notifyModuleSuccess(toast, "Материал ГО удален");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "удаление материала ГО", e);
    },
  });
  const downloadMaterial = async (ginfFileId: number, filename?: string | null) => {
    if (!selectedId) return;
    try {
      const blob = await goEntitiesApi.downloadMaterialBlob(selectedId, ginfFileId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename?.trim() || `go-material-${ginfFileId}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      notifyModuleError(toast, "скачивание материала ГО", e as Error);
    }
  };

  useEffect(() => {
    setNameInput(detailQuery.data?.obj_name || "");
    setSectionIdInput(
      detailQuery.data?.kls_id_section != null ? String(detailQuery.data.kls_id_section) : "none",
    );
    setLevel1IdInput(detailQuery.data?.kls_id_1 != null ? String(detailQuery.data.kls_id_1) : "none");
    setLevel2IdInput(detailQuery.data?.kls_id_2 != null ? String(detailQuery.data.kls_id_2) : "none");
    setEntityTypeIdInput(
      detailQuery.data?.entity_type_kls_id != null
        ? String(detailQuery.data.entity_type_kls_id)
        : "none",
    );
  }, [detailQuery.data]);

  const openCreateDialog = () => {
    const countryTypeId = metaQuery.data?.country_entity_type_kls_id;
    if (sidebarTab === "countries" && countryTypeId != null) {
      setCreateEntityTypeIdInput(String(countryTypeId));
    } else {
      setCreateEntityTypeIdInput("none");
    }
    setCreateDialogOpen(true);
  };

  const geometryToFeature = (g: Geometry): GeoJSON.Feature | null => {
    const gj = g.geojson;
    if (!gj) return null;
    if ((gj as GeoJSON.Feature).type === "Feature") return gj as GeoJSON.Feature;
    return { type: "Feature", properties: { id: g.id }, geometry: gj as GeoJSON.Geometry };
  };

  const buildPassportPrintRows = (fieldValues: GoEntityCardFieldValue[] | undefined) => {
    const byKey = new Map((fieldValues ?? []).map((f) => [f.field_key, f]));
    return COUNTRY_PASSPORT_FIELDS.map((def) => {
      let row = byKey.get(def.fieldKey);
      if (!row && def.altKeys) {
        for (const alt of def.altKeys) {
          const found = byKey.get(alt);
          if (found) {
            row = found;
            break;
          }
        }
      }
      const value = row?.is_na
        ? "⊘ Неприменимо"
        : formatPassportValue(row?.value, row?.is_masked);
      return { label: def.label, value };
    });
  };

  const printCountryPassport = async () => {
    if (selectedId == null || !detailQuery.data) return;
    setPassportPrintPending(true);
    try {
      const materials: GoMaterialItem[] = materialsQuery.data ?? [];
      const flagItem = materials.find((m) => m.material_kind === "flag") ?? null;
      const coatItem = materials.find((m) => m.material_kind === "coat") ?? null;
      let flagDataUrl: string | null = null;
      let coatDataUrl: string | null = null;
      if (flagItem) {
        const blob = await goEntitiesApi.downloadMaterialBlob(selectedId, flagItem.ginf_file_id);
        flagDataUrl = await blobToDataUrl(blob);
      }
      if (coatItem) {
        const blob = await goEntitiesApi.downloadMaterialBlob(selectedId, coatItem.ginf_file_id);
        coatDataUrl = await blobToDataUrl(blob);
      }
      const geojsonFeatures = overlayGeometries
        .map(geometryToFeature)
        .filter((f): f is GeoJSON.Feature => f != null);
      printGoCountryPassportReport({
        ginfObjId: detailQuery.data.ginf_obj_id,
        objName: detailQuery.data.obj_name || `Сущность #${detailQuery.data.ginf_obj_id}`,
        entityTypeName:
          detailQuery.data.entity_type_name ||
          (detailQuery.data.entity_type_kls_id != null
            ? `#${detailQuery.data.entity_type_kls_id}`
            : null),
        templateName: cardQuery.data?.template_resolved
          ? (cardQuery.data.template?.name ?? cardQuery.data.template?.code)
          : null,
        mapGeometryCount: detailQuery.data.map_geometry_count,
        passportRows: buildPassportPrintRows(cardQuery.data?.field_values),
        flagDataUrl,
        coatDataUrl,
        geojsonFeatures,
      });
    } catch (e) {
      notifyModuleError(toast, "печать карточки страны", e as Error);
    } finally {
      setPassportPrintPending(false);
    }
  };

  useEffect(() => {
    if (createSectionIdInput !== "none") return;
    const defaultSection = metaQuery.data?.section_options?.[0];
    if (defaultSection) {
      setCreateSectionIdInput(String(defaultSection.kls_id));
    }
  }, [createSectionIdInput, metaQuery.data?.section_options]);

  return (
    <>
    <MainWorkspaceLayout
      header={
        <Header
          className="sticky top-0 z-50"
          onSearch={(query) => {
            setSearchTerm(query);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (query) next.set("q", query);
                else next.delete("q");
                return next;
              },
              { replace: true },
            );
          }}
        />
      }
      sidebar={
        <aside
          className={cn(
            "flex min-h-0 w-[380px] flex-col border-r border-border bg-sidebar text-sidebar-foreground",
            chromeSelectClass,
          )}
        >
          <EntityListPaneHeader title="Географическое описание" hideTitle>
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <h2 className="truncate text-sm font-semibold">Географическое описание</h2>
                <SectionHelpTooltip label="Справка по сайдбару ГО">
                  <p className="font-medium">Зачем</p>
                  <p>
                    Два режима: реестр стран и навигация по разделам «Общие сведения» (структура ВГО
                    из KLS).
                  </p>
                  <p className="font-medium mt-2">Как работать</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>«Страны» — список стран (N в табе); клик открывает хаб справа</li>
                    <li>
                      Иконка фильтра — фильтры списка (УДГСПЗ, удалённые); не фильтры карты
                    </li>
                    <li>
                      Иконка «+» — диалог новой сущности; «корзина» — удалить выбранную запись
                    </li>
                    <li>
                      «Общие сведения» — дерево разделов 1.–7.; клик по узлу открывает сущности
                      раздела (если загружены)
                    </li>
                    <li>Выбор узла дерева снимает страну и показывает заглушку контента</li>
                    <li>Импорт стран / seed ok20 — админ-консоль ГО</li>
                    <li>Повторный клик / Esc снимает выбор страны</li>
                  </ul>
                  <p className="font-medium mt-2">Клавиатура</p>
                  <ul className="list-disc pl-4">
                    <li>Esc — выход из правки, затем снять выбор</li>
                    <li>Ctrl/Cmd+J — продолжить заполнение (классификатор)</li>
                    <li>Ctrl/Cmd+/ — справка по шорткатам</li>
                  </ul>
                </SectionHelpTooltip>
              </span>
              <div className="flex shrink-0 gap-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="relative h-7 w-7"
                      title="Фильтры списка"
                      aria-label="Фильтры списка"
                    >
                      <ListFilter className="h-3.5 w-3.5" />
                      {hasUdgFilter !== "all" || deletedFilter !== "active" ? (
                        <span
                          className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary"
                          aria-hidden
                        />
                      ) : null}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="z-[230] w-64 space-y-3 p-3">
                    <p className="text-sm font-medium text-foreground">Фильтры списка</p>
                    <p className="text-[11px] text-muted-foreground">
                      Относятся к реестру стран в сайдбаре, не к карте.
                    </p>
                    <div className="space-y-1">
                      <Label className="text-xs">УДГСПЗ</Label>
                      <Select
                        value={hasUdgFilter}
                        onValueChange={(v) => setHasUdgFilter(v as "all" | "yes" | "no")}
                      >
                        <SelectTrigger className="h-8" title="Фильтр стран с УДГСПЗ">
                          <SelectValue placeholder="УДГСПЗ" />
                        </SelectTrigger>
                        <SelectContent className="z-[250]">
                          <SelectItem value="all">УДГСПЗ: все</SelectItem>
                          <SelectItem value="yes">С УДГСПЗ</SelectItem>
                          <SelectItem value="no">Без УДГСПЗ</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Статус записей</Label>
                      <Select
                        value={deletedFilter}
                        onValueChange={(v) =>
                          setDeletedFilter(v as "active" | "include" | "deleted_only")
                        }
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Фильтр удаления" />
                        </SelectTrigger>
                        <SelectContent className="z-[250]">
                          <SelectItem value="active">Только активные</SelectItem>
                          <SelectItem value="include">Включая удалённые</SelectItem>
                          <SelectItem value="deleted_only">Только удалённые</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  title="Удалить выбранную сущность"
                  aria-label="Удалить выбранную сущность"
                  disabled={
                    selectedId == null || isSelectedDeleted || softDeleteMutation.isPending
                  }
                  onClick={() => {
                    if (selectedId == null || isSelectedDeleted) return;
                    setDeleteConfirmOpen(true);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  className="h-7 w-7"
                  title="Добавить сущность"
                  aria-label="Добавить сущность"
                  onClick={openCreateDialog}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <Tabs
              value={sidebarTab}
              onValueChange={(v) => setSidebarTab(v as GoSidebarTab)}
              className="mt-2"
            >
              <TabsList className="grid h-auto w-full grid-cols-2 gap-1 p-1">
                <TabsTrigger value="countries" className="text-xs px-2 py-1.5">
                  Страны ({countriesTotal})
                </TabsTrigger>
                <TabsTrigger value="general-info" className="text-xs px-2 py-1.5">
                  Общие сведения
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {sidebarTab === "countries" ? (
              <EntitySearchInput
                className="mt-2"
                placeholder="Поиск по странам..."
                value={searchTerm === "null" || searchTerm === "undefined" ? "" : searchTerm}
                onChange={(v) => setSearchTerm(v === "null" || v === "undefined" ? "" : v)}
              />
            ) : (
              <EntitySearchInput
                className="mt-2"
                placeholder="Поиск по разделам..."
                value={generalInfoSearch}
                onChange={setGeneralInfoSearch}
              />
            )}
          </EntityListPaneHeader>
          <div className="flex-1 overflow-y-auto">
            {sidebarTab === "countries" ? (
              <EntityListPanel
                embedded
                title=""
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                isLoading={listQuery.isLoading}
                isError={listQuery.isError}
                errorText="Ошибка загрузки реестра ГО. Проверьте доступ и API."
                emptyText="Реестр стран пуст: записи не найдены или недоступны по фильтру. Импорт — в админке ГО."
                items={entities}
                selectedId={selectedId}
                getId={(item) => item.ginf_obj_id}
                onSelect={(id) => {
                  setGeneralInfoSelection(null);
                  setSelectedId((prev) => (prev === id ? null : id));
                }}
                renderItem={(item) => (
                  <>
                    <div className="text-sm font-medium truncate">
                      {item.obj_name || `Сущность #${item.ginf_obj_id}`}
                      {item.is_del ? (
                        <span className="ml-2 text-xs font-normal text-destructive">удалено</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      Тип:{" "}
                      {item.entity_type_name ||
                        (item.entity_type_kls_id != null ? `#${item.entity_type_kls_id}` : "—")}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      Геометрий: {item.map_geometry_count}
                    </div>
                  </>
                )}
              />
            ) : (
              <GoGeneralInfoTree
                nodes={generalInfoTreeQuery.data ?? []}
                selectedKlsId={generalInfoSelection?.klsId ?? null}
                searchValue={generalInfoSearch}
                isLoading={generalInfoTreeQuery.isLoading}
                isError={generalInfoTreeQuery.isError}
                onSelect={(node: GoGeneralInfoTreeNode) => {
                  setSelectedId(null);
                  setGeneralInfoSelection({
                    klsId: node.kls_id,
                    code: node.code,
                    label: node.label,
                  });
                }}
              />
            )}
          </div>
        </aside>
      }
      workspace={
        <div className="absolute inset-0 min-h-0 bg-background">
          <ResizablePanelGroup
            direction="horizontal"
            autoSaveId="go-workspace-map-split"
            className="h-full w-full"
          >
            <ResizablePanel defaultSize={60} minSize={28} className="min-h-0 min-w-0">
              <div className="h-full min-h-0 overflow-auto p-4">
          {generalInfoSelection != null && selectedId == null ? (
            <Card className="max-w-2xl">
              <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                <CardTitle className="text-base">
                  <span className="text-muted-foreground mr-1.5 tabular-nums">
                    {generalInfoSelection.code}
                  </span>
                  {generalInfoSelection.label}
                </CardTitle>
                <SectionHelpTooltip label="Справка по разделу общих сведений">
                  <div className="space-y-2 text-sm">
                    <p>
                      Раздел структуры ВГО. Сущности (регионы, районы и др.) привязаны к каталогу
                      разделов модуля и показываются после импорта.
                    </p>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Клик по строке открывает карточку сущности</li>
                      <li>Повторный клик в дереве слева снимает выбор сущности</li>
                    </ul>
                  </div>
                </SectionHelpTooltip>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {generalInfoResolveQuery.isLoading || generalInfoEntitiesQuery.isLoading ? (
                  <p className="text-muted-foreground">Загрузка раздела…</p>
                ) : generalInfoResolveQuery.isError ? (
                  <p className="text-destructive">Не удалось проверить привязку раздела.</p>
                ) : generalInfoResolveQuery.data?.resolved !== true ? (
                  <p className="text-muted-foreground">
                    Навигационный узел без одноимённого раздела в каталоге сущностей. Список записей
                    для этого узла не формируется.
                  </p>
                ) : generalInfoEntitiesQuery.isError ? (
                  <p className="text-destructive">Ошибка загрузки сущностей раздела.</p>
                ) : (generalInfoEntitiesQuery.data?.items.length ?? 0) === 0 ? (
                  <p className="text-muted-foreground">
                    В разделе пока нет сущностей. Импорт регионов — в админке ГО (
                    «Регионы и районы»).
                  </p>
                ) : (
                  <ul className="divide-y rounded-md border">
                    {(generalInfoEntitiesQuery.data?.items ?? []).map((item) => (
                      <li key={item.ginf_obj_id}>
                        <button
                          type="button"
                          className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-muted/60"
                          onClick={() => {
                            setSelectedId(item.ginf_obj_id);
                            setSidebarTab("general-info");
                          }}
                        >
                          <span className="text-sm font-medium">
                            {item.obj_name || `#${item.ginf_obj_id}`}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {item.entity_type_name ||
                              (item.entity_type_kls_id != null
                                ? `#${item.entity_type_kls_id}`
                                : "—")}
                            {" · "}геометрий: {item.map_geometry_count}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-muted-foreground">
                  kls_id дерева: {generalInfoSelection.klsId}
                  {generalInfoResolveQuery.data?.section_kls_id != null
                    ? ` · раздел: ${generalInfoResolveQuery.data.section_kls_id}`
                    : null}
                </p>
              </CardContent>
            </Card>
          ) : isSelectedDeleted ? (
            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle>
                  {selectedEntity?.obj_name || `#${selectedId}`} · удалена
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>Сущность soft-deleted. Редактирование недоступно до восстановления.</p>
                <Button
                  disabled={restoreMutation.isPending}
                  onClick={() => restoreMutation.mutate()}
                >
                  {restoreMutation.isPending ? "Восстановление…" : "Восстановить"}
                </Button>
              </CardContent>
            </Card>
          ) : (
          <EntityDetailsShell
            selected={selectedId != null}
            selectedTitle={
              selectedEntity?.obj_name ||
              detailQuery.data?.obj_name ||
              (selectedId != null ? `#${selectedId}` : undefined)
            }
            emptyTitle="Выберите страну или раздел"
            emptyDescription="Слева: таб «Страны» или «Общие сведения». Выбор открывает хаб или список сущностей раздела."
            activeTab={activeTab}
            onTabChange={setActiveTab}
            headerActions={
              selectedId != null ? (
                <Button
                  type="button"
                  size="sm"
                  variant={cardEdit ? "default" : "outline"}
                  onClick={() => setCardEdit((v) => !v)}
                  aria-pressed={cardEdit}
                >
                  {cardEdit ? "Готово" : "Редактировать карточку"}
                </Button>
              ) : null
            }
            tabs={[
              {
                value: "general",
                label: "Общие сведения",
                content: (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3">
                      <CardTitle className="text-base inline-flex items-center gap-1.5">
                        Общие сведения
                        <SectionHelpTooltip label="Справка: источник и журнал карточки">
                          <p className="font-medium">Зачем</p>
                          <p>
                            У каждого поля — сведения об источнике (легитимность, наименование,
                            дата as-of). Журнал ниже фиксирует историю сохранений.
                          </p>
                          <p className="mt-2 font-medium">Как работать</p>
                          <ul className="list-disc pl-4">
                            <li>В просмотре: бейдж у поля показывает легитимность/источник.</li>
                            <li>
                              В правке: «Источник» у поля → popover → сохранить provenance отдельно
                              от значения.
                            </li>
                            <li>
                              «Журнал карточки» — кто и что менял (значение и provenance); это не
                              то же самое, что источник у поля.
                            </li>
                          </ul>
                        </SectionHelpTooltip>
                      </CardTitle>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-8 w-8 shrink-0"
                        title="Печать карточки страны (PDF)"
                        aria-label="Печать карточки страны (PDF)"
                        disabled={
                          selectedId == null ||
                          detailQuery.isLoading ||
                          !detailQuery.data ||
                          passportPrintPending
                        }
                        onClick={() => {
                          void printCountryPassport();
                        }}
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-4">
                      {detailQuery.isLoading ? (
                        <div>Загрузка данных...</div>
                      ) : detailQuery.data ? (
                        <>
                          <CountryPassportPanel
                            name={detailQuery.data.obj_name}
                            ginfObjId={detailQuery.data.ginf_obj_id}
                            isoHint={
                              (summaryQuery.data as { iso_alpha3?: string } | undefined)?.iso_alpha3 ??
                              null
                            }
                            entityTypeName={
                              detailQuery.data.entity_type_name ||
                              (detailQuery.data.entity_type_kls_id != null
                                ? `#${detailQuery.data.entity_type_kls_id}`
                                : null)
                            }
                            mapGeometryCount={detailQuery.data.map_geometry_count}
                            fieldValues={cardQuery.data?.field_values}
                            materials={materialsQuery.data}
                            templateName={
                              cardQuery.data?.template_resolved
                                ? (cardQuery.data.template?.name ?? cardQuery.data.template?.code)
                                : null
                            }
                            loading={cardQuery.isLoading}
                            editing={cardEdit}
                            highlightedFieldKey={highlightFieldKey}
                            savingFieldKeys={savingFieldKeys}
                            onCommit={commitCardField}
                            onMarkNa={markNaField}
                            onClearNa={clearNaField}
                            onSaveProvenance={saveFieldProvenance}
                            onFocusField={setHighlightFieldKey}
                          />

                          <Collapsible className="rounded-md border border-border">
                            <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/40 [&[data-state=open]>svg]:rotate-180">
                              <span>Журнал карточки</span>
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-2 border-t border-border px-3 py-3">
                              {cardAuditQuery.isLoading ? (
                                <p className="text-xs text-muted-foreground">Загрузка журнала…</p>
                              ) : (cardAuditQuery.data?.length ?? 0) === 0 ? (
                                <p className="text-xs text-muted-foreground">Записей пока нет</p>
                              ) : (
                                <ul className="space-y-2 text-xs">
                                  {(cardAuditQuery.data ?? []).map((row) => (
                                    <li key={row.id} className="rounded border bg-muted/30 p-2">
                                      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-muted-foreground">
                                        <span>
                                          {row.created_at
                                            ? new Date(row.created_at).toLocaleString("ru-RU")
                                            : "—"}
                                        </span>
                                        <span>
                                          {row.user_email ??
                                            row.user_username ??
                                            row.user_id ??
                                            "—"}
                                        </span>
                                      </div>
                                      <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-snug">
                                        {JSON.stringify(row.payload ?? {}, null, 2)}
                                      </pre>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </CollapsibleContent>
                          </Collapsible>

                          <Collapsible className="rounded-md border border-border">
                            <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/40 [&[data-state=open]>svg]:rotate-180">
                              <span>Настройки сущности</span>
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-3 border-t border-border px-3 py-3">
                              <p className="text-[11px] text-muted-foreground">
                                Служебные атрибуты реестра (не поля ok20). Раздел сейчас:{" "}
                                <span className="text-foreground">
                                  {detailQuery.data.section_name || "—"}
                                </span>
                              </p>
                              <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                                <div>
                                  <Label>Наименование в реестре</Label>
                                  <Input
                                    value={nameInput}
                                    onChange={(e) => setNameInput(e.target.value)}
                                    placeholder={detailQuery.data.obj_name || "Введите наименование"}
                                  />
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    onClick={() =>
                                      patchMutation.mutate({
                                        obj_name: nameInput || null,
                                        kls_id_section:
                                          sectionIdInput !== "none" ? Number(sectionIdInput) : null,
                                        kls_id_1:
                                          level1IdInput !== "none" ? Number(level1IdInput) : null,
                                        kls_id_2:
                                          level2IdInput !== "none" ? Number(level2IdInput) : null,
                                        entity_type_kls_id:
                                          entityTypeIdInput !== "none"
                                            ? Number(entityTypeIdInput)
                                            : null,
                                      })
                                    }
                                    disabled={patchMutation.isPending}
                                  >
                                    {patchMutation.isPending ? "Сохранение..." : "Сохранить"}
                                  </Button>
                                </div>
                              </div>
                              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                                <div className="space-y-1">
                                  <Label>Тип сущности</Label>
                                  <p className="text-[11px] text-muted-foreground">
                                    Определяет список (Страны / регион…)
                                  </p>
                                  <Select
                                    value={entityTypeIdInput}
                                    onValueChange={setEntityTypeIdInput}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Не выбрано" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[250]">
                                      <SelectItem value="none">Не выбрано</SelectItem>
                                      {(metaQuery.data?.entity_type_options ?? []).map((option) => (
                                        <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                                          {option.kls_names}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label>Раздел (KLS)</Label>
                                  <p className="text-[11px] text-muted-foreground">
                                    Раздел общих сведений, не тип
                                  </p>
                                  <Select value={sectionIdInput} onValueChange={setSectionIdInput}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Не выбрано" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[250]">
                                      <SelectItem value="none">Не выбрано</SelectItem>
                                      {(metaQuery.data?.section_options ?? []).map((option) => (
                                        <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                                          {option.kls_names}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label>Классификатор 1 (KLS)</Label>
                                  <Select value={level1IdInput} onValueChange={setLevel1IdInput}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Не выбрано" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[250]">
                                      <SelectItem value="none">Не выбрано</SelectItem>
                                      {(metaQuery.data?.level1_options ?? []).map((option) => (
                                        <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                                          {option.kls_names}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label>Классификатор 2 (KLS)</Label>
                                  <Select value={level2IdInput} onValueChange={setLevel2IdInput}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Не выбрано" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[250]">
                                      <SelectItem value="none">Не выбрано</SelectItem>
                                      {(metaQuery.data?.level2_options ?? []).map((option) => (
                                        <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                                          {option.kls_names}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        </>
                      ) : (
                        <div>Данные сущности не найдены</div>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              ...(["geography", "society", "economy"] as const).map((tab) => ({
                value: tab,
                label:
                  tab === "geography" ? "География" : tab === "society" ? "Общество" : "Экономика",
                content: (
                  <Card key={tab}>
                    <CardHeader>
                      <CardTitle className="text-base inline-flex items-center gap-1.5">
                        {tab === "geography"
                          ? "География"
                          : tab === "society"
                            ? "Общество"
                            : "Экономика"}
                        <SectionHelpTooltip label={`Справка вкладки ${tab}`}>
                          <p className="font-medium">Зачем</p>
                          <p>Поля шаблона ok20 с hub_tab={tab}.</p>
                          <p className="mt-2 font-medium">Как работать</p>
                          <ul className="list-disc pl-4">
                            <li>«Редактировать карточку» → правка; blur/переключатель сохраняет поле.</li>
                            <li>Виджет по типу поля (число, дата, да/нет, KLS).</li>
                            <li>«Источник» у поля — легитимность и дата сведений (as-of).</li>
                            <li>N/A у поля или Ctrl+Shift+N; «Снять N/A» возвращает ввод.</li>
                            <li>Esc — выход из правки, затем снятие выбора сущности.</li>
                            <li>Ctrl+/ — список шорткатов.</li>
                          </ul>
                        </SectionHelpTooltip>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                      {activeTab !== tab ? (
                        <div className="text-xs">Откройте вкладку для загрузки полей…</div>
                      ) : cardQuery.isLoading ? (
                        <div>Загрузка…</div>
                      ) : (cardQuery.data?.field_values?.length ?? 0) === 0 ? (
                        <div className="text-xs">
                          {cardQuery.data?.message ?? "Нет полей для этой вкладки (нужен seed ok20)."}
                        </div>
                      ) : (
                        cardQuery.data!.field_values.map((fv) => (
                          <GoCardFieldControl
                            key={fv.field_key}
                            field={fv}
                            editing={cardEdit}
                            highlighted={highlightFieldKey === fv.field_key}
                            saving={savingFieldKeys.has(fv.field_key)}
                            onCommit={commitCardField}
                            onMarkNa={markNaField}
                            onClearNa={clearNaField}
                            onSaveProvenance={saveFieldProvenance}
                            onFocusField={setHighlightFieldKey}
                          />
                        ))
                      )}
                    </CardContent>
                  </Card>
                ),
              })),
              {
                value: "classifier",
                label: "Классификатор",
                content: (
                  <Card>
                    <CardContent className="pt-4 text-sm text-muted-foreground">
                      {activeTab !== "classifier" ? (
                        <div className="text-xs">Откройте вкладку для загрузки классификатора…</div>
                      ) : (
                        <CountryClassifierPanel
                          fields={cardQuery.data?.field_values ?? []}
                          loading={cardQuery.isLoading}
                          message={cardQuery.data?.message}
                          editing={cardEdit}
                          highlightedFieldKey={highlightFieldKey}
                          savingFieldKeys={savingFieldKeys}
                          onCommit={commitCardField}
                          onMarkNa={markNaField}
                          onClearNa={clearNaField}
                          onSaveProvenance={saveFieldProvenance}
                          onFocusField={setHighlightFieldKey}
                          focusFieldKey={
                            activeTab === "classifier" ? highlightFieldKey : null
                          }
                          continueNonce={continueNonce}
                          onRequestPrepare={() => setPrepareOpen(true)}
                          onContinueResult={(result) => {
                            if (result === "done") {
                              notifyModuleSuccess(toast, "Все доступные поля заполнены");
                            }
                          }}
                        />
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "all-fields",
                label: "Все поля",
                content: (
                  <Card>
                    <CardContent className="pt-4 text-sm text-muted-foreground">
                      {activeTab !== "all-fields" ? (
                        <div className="text-xs">Откройте вкладку для загрузки всех полей…</div>
                      ) : (
                        <CountryFieldsBrowser
                          fields={cardQuery.data?.field_values ?? []}
                          loading={cardQuery.isLoading}
                          message={cardQuery.data?.message}
                          editing={cardEdit}
                          highlightedFieldKey={highlightFieldKey}
                          savingFieldKeys={savingFieldKeys}
                          onCommit={commitCardField}
                          onMarkNa={markNaField}
                          onClearNa={clearNaField}
                          onSaveProvenance={saveFieldProvenance}
                          onFocusField={setHighlightFieldKey}
                        />
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "geometries",
                label: "Геометрии",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Геометрии ГО</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                      <p className="text-xs">
                        Привязка к <code className="text-foreground">core.geometries.id</code>. Клик по объекту
                        ОИ на карте подставляет geometry id в форму.
                      </p>
                      <EntityGeometrySection<GoEntityMapGeometryItem>
                        addNameValue={bindGeometryName}
                        onAddNameChange={setBindGeometryName}
                        addGeometryIdValue={bindGeometryId}
                        onAddGeometryIdChange={setBindGeometryId}
                        onAdd={() => {
                          const id = Number(bindGeometryId);
                          if (!Number.isFinite(id) || id < 1) {
                            notifyModuleError(toast, "привязку геометрии ГО", new Error("Укажите корректный ID"));
                            return;
                          }
                          createMapGeometryMutation.mutate(id);
                        }}
                        addPending={createMapGeometryMutation.isPending}
                        isLoading={detailQuery.isLoading}
                        items={mapGeometries}
                        getKey={(item) => item.ginf_obj_map_geom_id}
                        renderName={(item) =>
                          `Геометрия #${item.map_geometry_id ?? item.ginf_obj_map_geom_id}`
                        }
                        renderGeometryId={(item) => item.map_geometry_id}
                        onFocus={async (item) => {
                          if (selectedId == null) return;
                          try {
                            const geometry = await goEntitiesApi.getMapGeometryContour(
                              selectedId,
                              item.ginf_obj_map_geom_id,
                            );
                            if (mapCollapsed) {
                              mapPanelRef.current?.expand();
                              setMapCollapsed(false);
                            }
                            setMapPreviewGeometry(geometry);
                            setMapPreviewNonce((n) => n + 1);
                            notifyModuleSuccess(toast, "Геометрия на карте модуля");
                          } catch (error) {
                            notifyModuleError(
                              toast,
                              "фокус геометрии ГО на карте",
                              error instanceof Error
                                ? error
                                : new Error(
                                    "Контур недоступен: привязка битая или геометрия удалена",
                                  ),
                            );
                          }
                        }}
                        onDelete={(item) => deleteMapGeometryMutation.mutate(item.ginf_obj_map_geom_id)}
                        deletePending={deleteMapGeometryMutation.isPending}
                        emptyText="Геометрии не привязаны"
                      />
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "geo-context",
                label: "Geo context",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Reverse-geocode</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-3">
                      <p className="text-xs">
                        Точка lon/lat → адресный summary (Nominatim) и опциональные попадания в geography.
                      </p>
                      <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                        <div className="space-y-1">
                          <Label>Longitude</Label>
                          <Input value={reverseLon} onChange={(e) => setReverseLon(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                          <Label>Latitude</Label>
                          <Input value={reverseLat} onChange={(e) => setReverseLat(e.target.value)} />
                        </div>
                        <div className="flex items-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!mapGeometries[0]?.map_geometry_id}
                            onClick={async () => {
                              const link = mapGeometries[0];
                              if (selectedId == null || link == null) return;
                              try {
                                const geometry = await goEntitiesApi.getMapGeometryContour(
                                  selectedId,
                                  link.ginf_obj_map_geom_id,
                                );
                                const coords = (geometry.geojson as GeoJSON.Feature)?.geometry;
                                // best-effort: use bbox center from first coordinate pair if polygon
                                const g = coords as GeoJSON.Polygon | GeoJSON.Point | undefined;
                                if (g?.type === "Point") {
                                  setReverseLon(String(g.coordinates[0]));
                                  setReverseLat(String(g.coordinates[1]));
                                } else if (g?.type === "Polygon" && g.coordinates[0]?.[0]) {
                                  setReverseLon(String(g.coordinates[0][0][0]));
                                  setReverseLat(String(g.coordinates[0][0][1]));
                                }
                              } catch (error) {
                                notifyModuleError(toast, "подстановку координат из геометрии", error);
                              }
                            }}
                          >
                            Из геометрии
                          </Button>
                          <Button
                            size="sm"
                            disabled={reverseGeocodeMutation.isPending}
                            onClick={() => reverseGeocodeMutation.mutate()}
                          >
                            {reverseGeocodeMutation.isPending ? "Поиск..." : "Найти"}
                          </Button>
                        </div>
                      </div>
                      {reverseGeocodeMutation.data ? (
                        <div className="space-y-1 rounded-md border border-border p-2 text-xs">
                          <div className="text-foreground font-medium">
                            {reverseGeocodeMutation.data.display_name || "Адрес не получен"}
                          </div>
                          <div>
                            {reverseGeocodeMutation.data.country || "-"} ·{" "}
                            {reverseGeocodeMutation.data.state || "-"} ·{" "}
                            {reverseGeocodeMutation.data.city || "-"}
                          </div>
                          <div>source: {reverseGeocodeMutation.data.source}</div>
                          {(reverseGeocodeMutation.data.geography_matches ?? []).length > 0 && (
                            <div className="pt-1 border-t border-border">
                              geography:{" "}
                              {reverseGeocodeMutation.data.geography_matches
                                .map((m) => m.name || `#${m.geography_id}`)
                                .join(", ")}
                            </div>
                          )}
                          {reverseGeocodeMutation.data.message && (
                            <div className="text-destructive">{reverseGeocodeMutation.data.message}</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs">Результат появится после запроса</div>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "region-links",
                label: "Регионы",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base inline-flex items-center gap-1.5">
                        Регионы / ИКР и связи УДГСПЗ
                        <SectionHelpTooltip label="Справка по регионам ГО">
                          <p>Иерархия из main.json без контуров в seed — честный UX «без геометрии».</p>
                          <p className="mt-1">
                            Импорт регионов — админ-консоль ГО. Связи УДГСПЗ — ниже на этой вкладке.
                          </p>
                        </SectionHelpTooltip>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-3">
                      <p className="text-xs rounded-md border border-dashed border-border px-2 py-1.5">
                        В пакете seed attribute geometry пустой — контуры регионов не создаём. На карте
                        регион без слоя выглядит без подсветки до появления геометрии.
                      </p>
                      {(linkedCountriesQuery.data?.length ?? 0) > 0 ? (
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-foreground">
                            Страны (OKSM)
                          </div>
                          <ul className="divide-y rounded-md border text-foreground">
                            {linkedCountriesQuery.data!.map((row) => (
                              <li
                                key={row.oksm_kls_id}
                                className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                              >
                                <span>
                                  {row.oksm_name || `OKSM #${row.oksm_kls_id}`}
                                  {row.country_obj_name &&
                                  row.country_obj_name !== row.oksm_name ? (
                                    <span className="text-muted-foreground">
                                      {" "}
                                      · {row.country_obj_name}
                                    </span>
                                  ) : null}
                                </span>
                                {row.country_ginf_obj_id != null ? (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-7 shrink-0"
                                    onClick={() => {
                                      setGeneralInfoSelection(null);
                                      setSidebarTab("countries");
                                      setSelectedId(row.country_ginf_obj_id!);
                                      setActiveTab("general");
                                    }}
                                  >
                                    Открыть
                                  </Button>
                                ) : (
                                  <span className="text-xs text-muted-foreground">нет в реестре</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      <RegionHierarchyHint
                        entityTypeOptions={metaQuery.data?.entity_type_options}
                        onOpen={(id) => {
                          setSelectedId(id);
                          setActiveTab("general");
                        }}
                      />
                      <div className="flex flex-wrap gap-2 items-end">
                        <div className="space-y-1">
                          <Label>eudg_id</Label>
                          <Input value={linkEudgId} onChange={(e) => setLinkEudgId(e.target.value)} />
                        </div>
                        <Button
                          size="sm"
                          disabled={createRegionLinkMutation.isPending || !selectedId}
                          onClick={() => createRegionLinkMutation.mutate()}
                        >
                          Связать
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={refreshRegionLinksMutation.isPending || !selectedId}
                          onClick={() => refreshRegionLinksMutation.mutate()}
                        >
                          Spatial refresh
                        </Button>
                      </div>
                      {regionLinksQuery.isLoading ? (
                        <div className="text-xs">Загрузка...</div>
                      ) : (regionLinksQuery.data?.items ?? []).length === 0 ? (
                        <div className="text-xs">Нет связей</div>
                      ) : (
                        <ul className="space-y-1">
                          {regionLinksQuery.data?.items.map((link) => (
                            <li
                              key={link.id}
                              className="flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5"
                            >
                              <div>
                                <div className="text-foreground">
                                  {link.eudg_name || `#${link.eudg_id}`}
                                </div>
                                <div className="text-xs">
                                  #{link.eudg_id} · {link.coverage}
                                  {link.is_auto_detected ? " · auto" : ""}
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={deleteRegionLinkMutation.isPending}
                                onClick={() => deleteRegionLinkMutation.mutate(link.id)}
                              >
                                Удалить
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "materials",
                label: "Материалы",
                content: (
                  <Card>
                    <CardContent className="pt-4 text-sm text-muted-foreground">
                      {selectedId != null ? (
                        <CountryMaterialsGallery
                          ginfObjId={selectedId}
                          items={materialsQuery.data ?? []}
                          isLoading={materialsQuery.isLoading}
                          addPending={uploadMaterialMutation.isPending}
                          deletePending={deleteMaterialMutation.isPending}
                          onUploadFile={(file, materialKind) =>
                            uploadMaterialMutation.mutate({ file, materialKind })
                          }
                          onDownload={(item) => void downloadMaterial(item.ginf_file_id, item.name)}
                          onDelete={(item) => deleteMaterialMutation.mutate(item.ginf_file_id)}
                        />
                      ) : null}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "compare",
                label: "Сравнение",
                content: (
                  <GoEntityComparePanel
                    leftId={selectedId}
                    rightId={compareRightId}
                    onRightIdChange={setCompareRightId}
                    entities={entities}
                  />
                ),
              },
            ]}
          />
          )}
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle className="w-1.5 bg-border hover:bg-primary/30" />
            <ResizablePanel
              ref={mapPanelRef}
              defaultSize={40}
              minSize={18}
              collapsible
              collapsedSize={4}
              onCollapse={() => setMapCollapsed(true)}
              onExpand={() => setMapCollapsed(false)}
              className="min-h-0 min-w-0"
            >
              <ModuleWorkspaceMapPanel
                title="Карта (ГО)"
                collapsed={mapCollapsed}
                onRequestCollapse={() => mapPanelRef.current?.collapse()}
                onRequestExpand={() => mapPanelRef.current?.expand()}
                overlayGeometries={overlayGeometries}
                selectedObject={overlaySelectedObject}
                onMvtSelect={({ geometryId }) => {
                  if (geometryId == null) return;
                  setActiveTab("geometries");
                  setBindGeometryId(String(geometryId));
                }}
                previewGeometry={mapPreviewGeometry}
                previewNonce={mapPreviewNonce}
                onPreviewConsumed={() => setMapPreviewGeometry(null)}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      }
    />
    <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <AlertDialogContent className="z-[230]">
        <AlertDialogHeader>
          <AlertDialogTitle>Удалить сущность ГО?</AlertDialogTitle>
          <AlertDialogDescription>
            Запись останется в системе и её можно восстановить. Продолжить удаление
            {selectedEntity?.obj_name ? ` «${selectedEntity.obj_name}»` : ""}?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={softDeleteMutation.isPending}>Отмена</AlertDialogCancel>
          <AlertDialogAction
            disabled={softDeleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(e) => {
              e.preventDefault();
              softDeleteMutation.mutate();
            }}
          >
            {softDeleteMutation.isPending ? "Удаление…" : "Удалить"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
      <DialogContent className="sm:max-w-md z-[220]">
        <DialogHeader>
          <DialogTitle>Новая сущность ГО</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1">
            <Label htmlFor="go-create-name">Наименование</Label>
            <Input
              id="go-create-name"
              value={createNameInput}
              onChange={(e) => setCreateNameInput(e.target.value)}
              placeholder="Наименование"
            />
          </div>
          <div className="grid gap-1">
            <Label>Тип сущности</Label>
            <p className="text-[11px] text-muted-foreground">
              Определяет список (Страны / регион…). С таба «Страны» по умолчанию — Страна.
            </p>
            <Select
              value={createEntityTypeIdInput}
              onValueChange={setCreateEntityTypeIdInput}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Тип" />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                <SelectItem value="none">Не выбрано</SelectItem>
                {(metaQuery.data?.entity_type_options ?? []).map((option) => (
                  <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                    {option.kls_names}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label>Раздел (KLS)</Label>
            <p className="text-[11px] text-muted-foreground">
              Классификатор раздела общих сведений, не тип сущности.
            </p>
            <Select value={createSectionIdInput} onValueChange={setCreateSectionIdInput}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Раздел" />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                <SelectItem value="none">Не выбрано</SelectItem>
                {(metaQuery.data?.section_options ?? []).map((option) => (
                  <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                    {option.kls_names}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label>KLS 1 (опц.)</Label>
            <Select value={createLevel1IdInput} onValueChange={setCreateLevel1IdInput}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Не задан" />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                <SelectItem value="none">Не задан</SelectItem>
                {(metaQuery.data?.level1_options ?? []).map((option) => (
                  <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                    {option.kls_names}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label>KLS 2 (опц.)</Label>
            <Select value={createLevel2IdInput} onValueChange={setCreateLevel2IdInput}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Не задан" />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                <SelectItem value="none">Не задан</SelectItem>
                {(metaQuery.data?.level2_options ?? []).map((option) => (
                  <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                    {option.kls_names}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setCreateDialogOpen(false)}
          >
            Отмена
          </Button>
          <Button
            type="button"
            disabled={
              createEntityMutation.isPending ||
              !createNameInput.trim() ||
              createEntityTypeIdInput === "none" ||
              createSectionIdInput === "none"
            }
            onClick={() =>
              createEntityMutation.mutate({
                obj_name: createNameInput.trim(),
                kls_id_section: Number(createSectionIdInput),
                entity_type_kls_id: Number(createEntityTypeIdInput),
                kls_id_1: createLevel1IdInput !== "none" ? Number(createLevel1IdInput) : null,
                kls_id_2: createLevel2IdInput !== "none" ? Number(createLevel2IdInput) : null,
              })
            }
          >
            {createEntityMutation.isPending ? "Создание..." : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {selectedId != null ? (
      <PrepareClassifierDialog
        open={prepareOpen}
        onOpenChange={setPrepareOpen}
        ginfObjId={selectedId}
        onApplied={() => {
          void queryClient.invalidateQueries({ queryKey: ["go-entities", "card", selectedId] });
        }}
      />
    ) : null}
    <Dialog open={shortcutsHelp} onOpenChange={setShortcutsHelp}>
      <DialogContent className="max-w-sm z-[220]">
        <DialogHeader>
          <DialogTitle>Шорткаты Country Hub</DialogTitle>
        </DialogHeader>
        <ul className="list-disc space-y-1 pl-4 text-sm text-muted-foreground">
          <li>
            <kbd className="text-foreground">Esc</kbd> — выход из правки, затем снять выбор
          </li>
          <li>
            <kbd className="text-foreground">Ctrl/Cmd+J</kbd> — продолжить заполнение (классификатор)
          </li>
          <li>
            <kbd className="text-foreground">Ctrl/Cmd+Shift+N</kbd> — N/A для сфокусированного поля
            (в режиме правки)
          </li>
          <li>
            <kbd className="text-foreground">Ctrl/Cmd+/</kbd> — эта справка
          </li>
        </ul>
      </DialogContent>
    </Dialog>
    </>
  );
}

